import { loadCadenas } from "@/lib/cadenas/loader";
import { reverseLookup } from "@/lib/cadenas/search";
import { EventBroker } from "@/lib/events";
import { environment } from "@/lib/resources";
import type { IWebViewer3DProps, showWebViewer3d } from "@cadenas/webcomponents";
import type { TransferObject } from "@cadenas/webcomponents/typings/core/core.interfaces";

type ArtikelDefinition = { projektdatei: string, artikelnummer: string; };
type OnVariableChangedArgs = Parameters<Exclude<IWebViewer3DProps["onVariableChanged"], undefined>>;

/** Kapselt einen Cadenas 3DViewer lazily.
 * Component wird erst initialisiert, wenn {@link load} aufgerufen wird, vorher wird der Artikel nur zwischengespeichert.
 */
export class Cadenas3DViewer {
	private readonly container: HTMLElement;
	private inner?: ReturnType<typeof showWebViewer3d>;
	private readonly options: IWebViewer3DProps;

	private artikel: ArtikelDefinition | undefined;

	constructor(container: HTMLElement, options: IWebViewer3DProps = {}) {
		this.container = container;
		this.options = options;

		this.options.onVariableChanged = (...args) => this.onVariableChanged.invoke(args);
	}

	public onVariableChanged = new EventBroker<OnVariableChangedArgs>();

	public clear(): void {
		this.artikel = undefined;
		this.update();
	}

	public setArtikel(artikel: ArtikelDefinition): void {
		this.artikel = artikel;
		this.update();
	}

	public async load(): Promise<void> {
		if (this.inner) return;

		// showWebViewer3d lässt sich den Zielcontainer nur als ID reingeben,
		// also einfach temporär eine setzen
		const tmpId = "cadenas-3d-container-tmp";
		let previousId = this.container.id;

		try {
			this.container.id = tmpId;
			this.options.masterObject = await this.getMasterObject();
			this.inner = (await loadCadenas()).showWebViewer3d(this.options, tmpId);
		} finally {
			this.container.id = previousId;
		}
	}

	private async update() {
		// Wenn das noch nicht geladen wurde, dann nichts tun
		if (!this.inner) return;

		this.options.masterObject = await this.getMasterObject();
		this.inner.update(this.options);
	}

	private async getMasterObject(): Promise<TransferObject> {
		if (!this.artikel) return {};

		let reverseMapResult = await reverseLookup(environment.cadenasCatalog!, this.artikel?.artikelnummer);

		if (reverseMapResult.error) {
			return {};
		}

		return { path: this.artikel.projektdatei, mident: reverseMapResult.mident };
	}
}
